import PropTypes from 'prop-types';
import { useCallback, useRef, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { getEntityData as getItemEntityData } from '@models/Item';
import { emptyObject } from '@constants/empty';
import useFetch from '@hooks/useFetch';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useClickAwayListener from '@hooks/useClickAwayListener';
import MenuLoader from './MenuLoader';
import PlannerMenuContent from './PlannerMenuContent';
import { eventPadding } from '../planner-constants';

const fetcherOptions = {
  useCamelCase: false,
  headers: {
    'X-Direct-To': 'sheldon',
    'X-PAGE': 'PLANNER'
  }
};

const StyledMenu = styled.div`
  position: fixed;
  z-index: 1;
  width: 200px;
  border: 1px solid var(--color-shadow);
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  padding: ${Spacings.SPACING_2B} 0;
  top: ${({ $top }) => `${$top - eventPadding}px`};
  left: ${({ $left }) => `${$left}px`};
`;

const PlannerItemMenu = ({
  itemAnchorEl,
  selectedItemRef,
  closeActionMenu
}) => {
  const {
    query: { goalUID }
  } = useRouter();

  const [isClickAwayActive, setIsClickAwayActive] = useState(true);
  const selectedItem = selectedItemRef.current || emptyObject;
  const isLesson = selectedItem.isPost;
  const { isTest, isOffline } = selectedItem;
  const isPastItem = (isLesson || isTest) && !selectedItem.isUpcoming;
  const endpoint = isLesson
    ? `v1/uplus/classes/${selectedItem.uid}/info_v2/`
    : `v2/quizzes/planner/${selectedItem.uid}/info/`;
  // const [endpointKey, setEndpointKey] = useState(null);
  // useEffect(() => {
  //   if (isPastItem && !endpointKey) setEndpointKey(endpoint);
  // }, [isPastItem, endpoint, endpointKey]);
  const endpointKey = isPastItem ? endpoint : null;
  const { mutate } = useSWRConfig();
  const onSuccess = useCallback(
    (data, key) => {
      const item = getItemEntityData(data);
      mutate(
        key,
        {
          ...item,
          programme: item.programme.course
        },
        false
      );
    },
    [mutate]
  );
  const { data: itemDetailsObject, isLoading } = useFetch(
    endpointKey,
    fetcherOptions,
    {
      onSuccess,
      revalidateIfStale: true
    }
  );

  const wrapperRef = useRef(null);
  useClickAwayListener(wrapperRef, () => {
    if (isClickAwayActive) {
      closeActionMenu();
    }
  });

  const position = useMemo(() => {
    if (!itemAnchorEl) return { x: 0, y: 0 };
    const { x, y } = itemAnchorEl.getBoundingClientRect();
    return { x: x + 52, y };
  }, [itemAnchorEl]);
  return (
    <StyledMenu ref={wrapperRef} $left={position.x} $top={position.y}>
      {endpointKey && isLoading ? (
        <MenuLoader />
      ) : (
        <PlannerMenuContent
          goalUID={goalUID}
          closeActionMenu={closeActionMenu}
          itemInfo={
            isPastItem ? itemDetailsObject || selectedItem : selectedItem
          }
          author={selectedItem.author}
          isLesson={isLesson}
          isTest={isTest}
          endpoint={endpoint}
          setClickAwayActive={setIsClickAwayActive}
          isOfflineCourseItem={isOffline}
        />
      )}
    </StyledMenu>
  );
};

PlannerItemMenu.propTypes = {
  itemAnchorEl: PropTypes.oneOfType([
    // PropTypes.instanceOf(HTMLElement),
    PropTypes.object
  ]),
  selectedItemRef: PropTypes.objectOf(),
  closeActionMenu: PropTypes.func.isRequired
};

PlannerItemMenu.defaultProps = {
  itemAnchorEl: null,
  selectedItemRef: null
};

export default PlannerItemMenu;
